import ExcelJS from 'exceljs';

/**
 * Read a bank statement workbook into the rows the reconciliation screen shows.
 *
 * Written against a real ICICI "Detailed Statement" export, whose shape is:
 * fifteen-odd rows of account detail, then a header row, then the transactions.
 * Nothing here depends on a column being in a particular place — the header row
 * is found by what it says and the columns are picked out by name, so a bank
 * that orders them differently still reads.
 *
 * The hard part is not the spreadsheet, it is the remark. A bank writes the
 * counterparty into it differently for every channel it settles through, and
 * truncates the name while doing so, so `extractParty` and `mergeTruncated`
 * below carry the real knowledge in this file.
 */

/* ── the spreadsheet ──────────────────────────────────────────────────────── */

const HEADER_HINTS = [/transaction\s*remarks?/i, /narration/i, /description/i];

/** Which column holds what, found by its heading rather than its position. */
const COLUMNS = {
  date: [/value\s*date/i, /transaction\s*date/i, /^date$/i],
  remark: [...HEADER_HINTS],
  debit: [/withdrawal/i, /^debit/i, /dr\s*amount/i],
  credit: [/deposit/i, /^credit/i, /cr\s*amount/i],
  ref: [/cheque|ref/i],
};

const cellText = (v) => {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) {
    const p = (n) => String(n).padStart(2, '0');
    return `${p(v.getDate())}/${p(v.getMonth() + 1)}/${v.getFullYear()}`;
  }
  if (typeof v === 'object') return String(v.text ?? v.result ?? v.richText?.map((r) => r.text).join('') ?? '');
  return String(v);
};

const cellNumber = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(typeof v === 'object' ? (v.result ?? v.text ?? 0) : v);
  return Number.isFinite(n) ? n : 0;
};

/** The transactions start under the row that names the remark column. */
const findHeaderRow = (sheet) => {
  const limit = Math.min(sheet.rowCount, 60);
  for (let r = 1; r <= limit; r += 1) {
    const texts = [];
    sheet.getRow(r).eachCell({ includeEmpty: false }, (c) => texts.push(cellText(c.value)));
    if (texts.some((t) => HEADER_HINTS.some((re) => re.test(t)))) {
      const map = {};
      sheet.getRow(r).eachCell({ includeEmpty: false }, (c, col) => {
        const t = cellText(c.value);
        Object.entries(COLUMNS).forEach(([key, patterns]) => {
          if (map[key] === undefined && patterns.some((re) => re.test(t))) map[key] = col;
        });
      });
      if (map.remark && (map.debit || map.credit)) return { row: r, map };
    }
  }
  return null;
};

/** `Name: ABC` and the period, read off the rows above the table. */
const readAccountHeader = (sheet, headerRow) => {
  const out = { holder: '', period: '' };
  for (let r = 1; r < headerRow; r += 1) {
    const cells = [];
    sheet.getRow(r).eachCell({ includeEmpty: false }, (c) => cells.push(cellText(c.value).trim()));
    for (let i = 0; i < cells.length - 1; i += 1) {
      if (/^name:?$/i.test(cells[i]) && !out.holder) out.holder = cells[i + 1];
      if (/transaction period/i.test(cells[i]) && !out.period) out.period = cells[i + 1];
    }
  }
  return out;
};

/* ── which bank ───────────────────────────────────────────────────────────── */

/**
 * The banks a statement is accepted from.
 *
 * The rules below were read off ICICI's "Detailed Statement", and a remark is
 * written differently by every bank, so a statement from another one is read
 * with ICICI's rules and whatever they cannot place lands in Miscellaneous —
 * where it can still be pointed at a party by hand. Each bank earns its own
 * rules as a real statement from it turns up.
 */
export const RULES_READ_FROM = 'ICICI Bank';

export const BANKS = [
  'ICICI Bank',
  'HDFC Bank',
  'Axis Bank',
  'State Bank of India',
  'Kotak Mahindra Bank',
  'Punjab National Bank',
  'Bank of Baroda',
  'Canara Bank',
  'Union Bank of India',
  'IndusInd Bank',
  'IDFC FIRST Bank',
  'Yes Bank',
  'IDBI Bank',
  'Federal Bank',
  'RBL Bank',
];

/* ── the remark ───────────────────────────────────────────────────────────── */

/** Channels that move money without a counterparty: cash, ATM, tax, returns. */
const NO_PARTY = new Set(['CAM', 'NFS', 'GIB', 'CMS', 'VAT', 'TAX']);

const channelOf = (remark) => remark.split('-')[0].split('/')[0].trim().toUpperCase();

/**
 * Pull the other side of the transaction out of a remark.
 *
 * Each rule below was read off a real statement; the comment is the shape it
 * matches. The credit/debit split matters more than once — the same channel
 * writes the name in a different place depending on which way the money went.
 */
export const extractParty = (remark, isCredit) => {
  const text = String(remark || '').trim();
  if (!text || /^REJECT/i.test(text)) return null;

  const channel = channelOf(text);
  if (NO_PARTY.has(channel)) return null;

  const clean = (s) => (s || '').trim().replace(/\s+/g, ' ') || null;

  if (channel === 'RTGS' || channel === 'NEFT') {
    // in:  RTGS-IDIBR5202511084852218-SAURABH KUMAR-00000008141360544-IDIB000B027
    if (text.includes('-')) {
      const p = text.split('-');
      return p.length > 2 ? clean(p[2]) : null;
    }
    // out: RTGS/ICICR42026061100510969/HDFC0000439/R B RAMNATH LAMBAH AND SONS PVT
    const p = text.split('/').map((x) => x.trim()).filter(Boolean);
    return p.length > 2 ? clean(p[p.length - 1]) : null;
  }

  const p = text.split('/').map((x) => x.trim()).filter(Boolean);

  switch (channel) {
    // CLG/ROHIT ENTERPRISES/034413/PNB/08.05.2026…   IPS/SHANKAR AUT/…
    case 'CLG':
    case 'IPS':
      return p.length > 1 ? clean(p[1]) : null;

    // BIL/ONL/001112038072/BSES Yamun/electricity bil
    case 'BIL':
      return p.length > 3 ? clean(p[3]) : null;

    // INF/NEFT/IN42614253947708/HDFC0000439/INV 604 AND686/RBRAMNATHLAMBAH
    case 'INF':
      return p.length > 1 ? clean(p[p.length - 1]) : null;

    // MMT/IMPS/531810980978/IMPS transactio/HMR SPEDIT/HDFC Bank
    // MMT/IMPS/531220350533/HMRHDFC/HDFC0000457
    // Either way the last field is the bank or its IFSC, so the name is before it.
    case 'MMT':
      return p.length > 2 ? clean(p[p.length - 2]) : null;

    // The two directions are written back to front:
    //   in:  UPI/HONEY SA/9910920557@pth/Sent using/HDFC BANK/…
    //   out: UPI/108569870984/UPI Pay/8130421810-2@ib//ICI…/
    case 'UPI':
      return p.length > 3 ? clean(isCredit ? p[1] : p[3]) : null;

    default:
      return null;
  }
};

/* ── dates ───────────────────────────────────────────────────────────────── */

const MONTHS = { jan:1, feb:2, mar:3, apr:4, may:5, jun:6, jul:7, aug:8, sep:9, oct:10, nov:11, dec:12 };
const pad = (n) => String(n).padStart(2, '0');

/**
 * The API wants YYYY-MM-DD. A statement writes the day first and the month as
 * a name as often as a number — 08/Nov/2025 and 08/11/2025 are both ICICI.
 */
export const toIsoDate = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);

  const parts = text.split(/[\/\-.\s]+/).filter(Boolean);
  if (parts.length < 3) return '';
  const [d, m, y] = parts;
  const month = /^\d+$/.test(m) ? Number(m) : MONTHS[m.slice(0, 3).toLowerCase()];
  const year = y.length === 2 ? 2000 + Number(y) : Number(y);
  if (!month || !year || !Number(d)) return '';
  return `${year}-${pad(month)}-${pad(Number(d))}`;
};

/* ── the whole job ───────────────────────────────────────────────────────── */

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * `parseBankStatementFile(file)` → the rows the API is posted.
 *
 * Reading the sheet and pulling the party out of each remark is all that
 * happens here. Deciding who that party is, remembering the answer and
 * flagging what could not be placed are the server's, which does it against
 * every user and the names it has already learned.
 *
 * A line with no counterparty — cash, ATM, tax, a returned cheque — is left
 * out, since there is nobody for it to be settled against.
 */
export async function parseBankStatementFile(file, bank = '') {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());

  const sheet = workbook.worksheets[0];
  if (!sheet) throw new Error('This workbook has no sheets');

  const header = findHeaderRow(sheet);
  if (!header) {
    throw new Error('Could not find the transactions — no row names a remark, a withdrawal and a deposit column');
  }
  const { row: headerRow, map } = header;
  const account = readAccountHeader(sheet, headerRow);

  const rows = [];
  let skippedNoParty = 0;
  let skippedNoDate = 0;

  for (let r = headerRow + 1; r <= sheet.rowCount; r += 1) {
    const row = sheet.getRow(r);
    const remark = cellText(row.getCell(map.remark).value).trim();
    const debit = map.debit ? cellNumber(row.getCell(map.debit).value) : 0;
    const credit = map.credit ? cellNumber(row.getCell(map.credit).value) : 0;
    if (!remark && !debit && !credit) continue;
    if (debit <= 0 && credit <= 0) continue;

    const name = extractParty(remark, credit > 0);
    if (!name) { skippedNoParty += 1; continue; }

    const date = toIsoDate(map.date ? cellText(row.getCell(map.date).value) : '');
    if (!date) { skippedNoDate += 1; continue; }

    rows.push({
      name,
      date,
      remark,
      ...(credit > 0 ? { credit: round2(credit) } : { debit: round2(debit) }),
    });
  }

  return {
    fileName: file.name,
    bank,
    period: account.period || '',
    holder: account.holder || '',
    rows,
    skipped: skippedNoParty + skippedNoDate,
  };
}

export default parseBankStatementFile;
